import {
  ActionIcon,
  Button,
  Input,
  TextInput,
  Textarea,
  Select,
  Badge,
  Indicator,
  CloseButton,
  Autocomplete,
  Switch,
  Tooltip,
  Checkbox,
  createTheme,
  DEFAULT_THEME,
  mergeMantineTheme,
  rem,
  Radio,
  RadioGroupProps,
  Pagination,
  Paper,
  NumberInput,
  Notification,
  Menu,
  Alert,
  PinInput
} from '@mantine/core';
import {
  DateInput,
  DatePicker,
  DatePickerInput,
  MonthPickerInput,
  TimeInput,
  YearPickerInput
} from '@mantine/dates';

import {
  ButtonOverrideClassNames,
  CloseButtonVarsResolver,
  DefaultCloseButtonProps,
  DefaultButtonProps,
  ButtonVarsResolver,
  ActionIconOverrideClassNames,
  ActionIconVarsResolver,
  DefaultActionIconProps
} from '../buttons';
import {
  DefaultIndicatorProps,
  IndicatorOverrideClassNames,
  DefaultBadgeProps
} from '../datadisplay';
import {
  AlertVarsResolver,
  DefaultAlertProps,
  NotificationDefaultProps
} from '../feedback';
import {
  DefaultInputProps,
  DefaultSelectProps,
  InputVarsResolver,
  SwitchOverrideClassNames,
  DefaultSwitchProps,
  DateInputVarsResolver,
  DefaultDateInputProps,
  DefaultRadioProps,
  DefaultCheckboxProps,
  CheckboxGroupProps,
  InputWrapperVarsResolver,
  InputWrapperOverrideClassNames,
  DefaultPinInputProps,
  PinInputVarsResolver
} from '../inputs';
import {
  DefaultPaperProps,
  PaperOverridesClassNames,
  PaperVarsResolver
} from '../misc';
import {
  DefaultPaginationProps,
  PaginationOverrideClassnames,
  PaginationVarsResolver
} from '../navigation';
import { DefaultTooltipProps, MenuOverrideClassNames } from '../overlays';

import { colors } from './colors';

const theme = createTheme({
  fontFamily: 'Inter, sans-serif',
  colors: colors,
  primaryColor: 'primary',
  primaryShade: 6,
  headings: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '600'
  },
  defaultRadius: 'md',
  radius: {
    xs: rem(6),
    sm: rem(8),
    md: rem(12),
    lg: rem(16),
    xl: rem(20)
  },
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem'
  },
  // need to check if this is causing issues elsewhere
  fontSizes: {
    xs: rem(12),
    sm: rem(13),
    md: rem(14),
    lg: rem(15),
    xl: rem(16)
  },
  respectReducedMotion: true,
  defaultGradient: { from: '#0E8DF4', to: '#0E85EF', deg: 180 },
  other: {
    drawerHeaderHeight: rem(63),
    drawerFooterHeight: rem(63)
  },
  components: {
    Button: Button.extend({
      defaultProps: DefaultButtonProps,
      classNames: ButtonOverrideClassNames,
      vars: ButtonVarsResolver
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: DefaultActionIconProps,
      classNames: ActionIconOverrideClassNames,
      vars: ActionIconVarsResolver
    }),
    Input: Input.extend({
      defaultProps: DefaultInputProps,
      vars: InputVarsResolver
    }),
    InputWrapper: Input.Wrapper.extend({
      vars: InputWrapperVarsResolver,
      classNames: InputWrapperOverrideClassNames
    }),
    TextInput: TextInput.extend({
      defaultProps: DefaultInputProps
    }),
    Select: Select.extend({
      defaultProps: DefaultSelectProps
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: DefaultSelectProps
    }),
    Badge: Badge.extend({
      defaultProps: DefaultBadgeProps
    }),
    Indicator: Indicator.extend({
      defaultProps: DefaultIndicatorProps,
      classNames: IndicatorOverrideClassNames
    }),
    Switch: Switch.extend({
      defaultProps: DefaultSwitchProps,
      classNames: SwitchOverrideClassNames
    }),
    DateInput: DateInput.extend({
      defaultProps: DefaultDateInputProps,
      vars: DateInputVarsResolver
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: DefaultDateInputProps,
      vars: DateInputVarsResolver
    }),
    YearPickerInput: YearPickerInput.extend({
      defaultProps: DefaultDateInputProps
    }),
    MonthPickerInput: MonthPickerInput.extend({
      defaultProps: DefaultDateInputProps
    }),
    DatePicker: DatePicker.extend({
      defaultProps: DefaultDateInputProps,
      vars: DateInputVarsResolver
    }),
    TimeInput: TimeInput.extend({
      defaultProps: DefaultInputProps,
      vars: InputVarsResolver
    }),
    Textarea: Textarea.extend({
      defaultProps: DefaultInputProps
    }),
    Tooltip: Tooltip.extend({
      defaultProps: DefaultTooltipProps
    }),
    Radio: Radio.extend({
      defaultProps: DefaultRadioProps
    }),
    RadioGroup: Radio.Group.extend({
      defaultProps: DefaultRadioProps as Partial<RadioGroupProps>
    }),
    Checkbox: Checkbox.extend({
      defaultProps: DefaultCheckboxProps
    }),
    CheckboxGroup: Checkbox.Group.extend({
      defaultProps: DefaultCheckboxProps as Partial<CheckboxGroupProps>
    }),
    Pagination: Pagination.extend({
      defaultProps: DefaultPaginationProps,
      classNames: PaginationOverrideClassnames,
      vars: PaginationVarsResolver
    }),
    CloseButton: CloseButton.extend({
      defaultProps: DefaultCloseButtonProps,
      vars: CloseButtonVarsResolver
    }),
    Alert: Alert.extend({
      vars: AlertVarsResolver,
      defaultProps: DefaultAlertProps
    }),
    Paper: Paper.extend({
      defaultProps: DefaultPaperProps,
      classNames: PaperOverridesClassNames,
      vars: PaperVarsResolver
    }),
    NumberInput: NumberInput.extend({
      defaultProps: DefaultInputProps,
      vars: InputVarsResolver
    }),
    Menu: Menu.extend({
      classNames: MenuOverrideClassNames
    }),
    Notification: Notification.extend({
      defaultProps: NotificationDefaultProps
    }),
    PinInput: PinInput.extend({
      defaultProps: DefaultPinInputProps,
      vars: PinInputVarsResolver
    })
  }
});

// this may not be needed. https://mantine.dev/theming/theme-object/#access-theme-outside-of-components
export const uiTheme = mergeMantineTheme(DEFAULT_THEME, theme);

/*Mantine Breakpoints

 xs		  576px
 sm 	  768px
 md		  992px
 lg		  1200px
 xl		  1408px
 
 */
